import { PauseIcon, PlayIcon, XMarkIcon } from "@heroicons/react/24/outline"
import { useEffect, useRef, useState } from "react"
import { toast } from "react-toastify"
import { useSetAssetThumbnailMutation } from "~/ui-rtk/api/assetsApi"
import type { ScrapedImage } from "../types"

/**
 * Pick a video's thumbnail from the video itself: scrub to any frame and
 * save it as the asset's thumbnail.
 *
 * The video is played straight from its URL with a plain (non-CORS) <video>
 * — the media bucket serves that fine. The capture itself happens
 * SERVER-SIDE: we send only the chosen timestamp to
 * POST /assets/:id/thumbnail (no `imageBase64`) and the API pulls the video
 * from S3 and extracts the frame with ffmpeg. This is deliberate: the bucket
 * sends no CORS headers, so a browser-side canvas capture is impossible
 * (CORS-mode loads fail, plain loads taint the canvas), and proxying whole
 * videos through the API as base64 was worse. The saved frame becomes what
 * Meta receives as the video's `thumbnail_url` when the card ships in a
 * (carousel) blast, and what the card/preview shows here.
 *
 * Requires a library asset (`asset.assetId`) — the frame is stored against
 * the asset row, so local-only blob fallbacks have nowhere to persist it.
 */

interface VideoThumbnailPickerModalProps {
  /** The video card to pick a thumbnail for (must carry `assetId`). */
  asset: ScrapedImage
  onClose: () => void
  /** Fired after a successful save with the fresh thumbnail URL. */
  onSaved: (thumbnailUrl: string) => void
}

const formatTime = (t: number): string => {
  if (!isFinite(t)) return "0:00.0"
  const m = Math.floor(t / 60)
  const s = t - m * 60
  return `${m}:${s.toFixed(1).padStart(4, "0")}`
}

export default function VideoThumbnailPickerModal({
  asset,
  onClose,
  onSaved,
}: VideoThumbnailPickerModalProps) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [duration, setDuration] = useState(0)
  const [currentTime, setCurrentTime] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [setThumbnail, { isLoading: isSaving }] = useSetAssetThumbnailMutation()

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [onClose])

  const togglePlay = () => {
    const video = videoRef.current
    if (!video) return
    if (video.paused) {
      void video.play()
    } else {
      video.pause()
    }
  }

  const seekTo = (t: number) => {
    const video = videoRef.current
    if (!video) return
    video.pause()
    video.currentTime = t
  }

  const captureAndSave = async () => {
    if (!asset.assetId) return
    try {
      // No imageBase64 — the server extracts this frame from the S3 video
      // itself, so the bucket's missing CORS headers don't matter.
      const updated = await setThumbnail({
        assetId: asset.assetId,
        frameTimeSec: Math.round(currentTime * 10) / 10,
      }).unwrap()

      if (updated.thumbnailUrl) {
        onSaved(updated.thumbnailUrl)
      }
      toast.success("Thumbnail updated — this frame now fronts the video")
      onClose()
    } catch (err) {
      const message =
        (err as { data?: { message?: string } })?.data?.message ??
        (err instanceof Error ? err.message : null)
      toast.error(message || "Failed to save thumbnail")
    }
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90"
      onClick={onClose}
    >
      <div
        className="relative flex flex-col gap-3 w-full max-w-2xl mx-4 p-4 rounded-2xl bg-system-gray-900 border border-system-gray-700/60"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-white">
            Pick a thumbnail frame
          </h3>
          <button
            onClick={onClose}
            aria-label="Close"
            className="w-7 h-7 rounded-full flex items-center justify-center bg-system-gray-800 hover:bg-system-gray-700 text-white transition"
          >
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>

        <div className="relative rounded-xl overflow-hidden bg-black max-h-[60vh] flex items-center justify-center">
          {/* Plain load on purpose — no crossOrigin. Playback doesn't need
              CORS, and the capture happens server-side (see header). */}
          <video
            key={asset.url}
            ref={videoRef}
            src={asset.url}
            muted
            playsInline
            preload="auto"
            className="max-h-[60vh] max-w-full"
            onLoadedMetadata={e => setDuration(e.currentTarget.duration || 0)}
            onTimeUpdate={e => setCurrentTime(e.currentTarget.currentTime)}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onError={() =>
              setLoadError(
                "The browser can't play this video's format (often a .mov from an iPhone or editing tool). You can still save the first frame as the thumbnail, or re-export the file as MP4 (H.264) to scrub it.",
              )
            }
            onClick={togglePlay}
          />
          {loadError && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/70 px-6 text-center text-xs text-system-gray-300">
              {loadError}
            </div>
          )}
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={togglePlay}
            aria-label={isPlaying ? "Pause" : "Play"}
            className="w-8 h-8 shrink-0 rounded-full flex items-center justify-center bg-system-gray-800 hover:bg-system-gray-700 text-white transition"
          >
            {isPlaying ? (
              <PauseIcon className="w-4 h-4" />
            ) : (
              <PlayIcon className="w-4 h-4" />
            )}
          </button>
          <input
            type="range"
            min={0}
            max={duration || 0}
            step={0.1}
            value={currentTime}
            onChange={e => seekTo(Number(e.target.value))}
            className="range range-xs range-primary flex-1"
            aria-label="Scrub video"
          />
          <span className="shrink-0 text-[11px] tabular-nums text-system-gray-400">
            {formatTime(currentTime)} / {formatTime(duration)}
          </span>
        </div>

        <div className="flex items-center justify-between gap-3">
          <p className="text-[11px] text-system-gray-500">
            Scrub (or play and pause) to the frame you want — it becomes the
            cover Meta shows before the video plays.
          </p>
          <button
            onClick={captureAndSave}
            disabled={isSaving || !asset.assetId}
            className="btn btn-primary btn-sm shrink-0"
          >
            {isSaving ? (
              <>
                <span className="loading loading-spinner loading-xs" />
                Saving…
              </>
            ) : (
              "Use this frame"
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
